import React from 'react';
import { Button } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { FixProposal } from '../../types';
import css from './TailorFixCard.module.scss';

interface Props {
  proposal: FixProposal;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
  /** True only while this apply is still revertible (it was the latest content
   *  mutation — see the snapshot's revision guard in ResumeTailor). */
  undoable?: boolean;
  onUndo?: () => void;
}

// Add ops carry an empty `before` — there is no current text being changed, so
// the "Current placement" placeholder (meant for moves/reorders) must not render.
const ADD_OPS = new Set(['add_bullet', 'add_list_item']);

// Delete ops carry an empty `after`, so without a hint the card reads as
// "keep this text". Flag them so the removed `before` is legible as a removal.
const DELETE_OPS = new Set([
  'delete_bullet',
  'delete_experience',
  'delete_list_item',
  'delete_contact_link',
  'delete_education',
  // Clears the graduation dates — its canonical `before` is the dates string
  // being removed, so "This will be removed." is accurate.
  'remove_education_dates',
]);

const StructuredProposalCard: React.FC<Props> = ({
  proposal,
  busy,
  onApply,
  onDismiss,
  undoable = false,
  onUndo,
}) => {
  const status = proposal.status ?? 'ready';
  const patches = proposal.patches ?? [];
  if (status === 'applied' && undoable) {
    // Mirror the legacy decided receipt: one calm line, the undo within reach.
    return (
      <div className={`${css.card} ${css.decided}`} role="status">
        <div className={css.decidedRow}>
          <CheckCircleFilled className={css.decidedIconDone} aria-hidden="true" />
          <span className={css.decidedLabel}>Applied</span>
          <span className={css.decidedIssue}>{proposal.reason}</span>
          <Button type="link" size="small" disabled={busy} onClick={onUndo}>
            Undo
          </Button>
        </div>
      </div>
    );
  }
  if (status === 'applied' || status === 'dismissed' || status === 'rejected') return null;
  if (status === 'stale') {
    return <div className={`${css.card} ${css.failed}`}><p className={css.issue}>This suggestion is no longer current.</p><p className={css.rationale}>{proposal.validation_reason ?? 'Your resume changed before it could be applied.'}</p></div>;
  }
  return (
    <div className={css.card}>
      <div className={css.header}><span className={css.category}>Suggested change</span></div>
      <p className={css.issue}>{proposal.reason}</p>
      {patches.map((patch, index) => (
        <div className={css.diff} key={`${patch.operation}-${index}`}>
          {!ADD_OPS.has(patch.operation) && (
            <p className={css.before}>{patch.before || 'Current placement'}</p>
          )}
          {!DELETE_OPS.has(patch.operation) && patch.after && <p className={css.after}>{patch.after}</p>}
          {patch.operation === 'reorder_bullets' && <p className={css.rationale}>Reorders the existing bullets.</p>}
          {DELETE_OPS.has(patch.operation) && <p className={css.rationale}>This will be removed.</p>}
        </div>
      ))}
      <div className={css.actions}>
        <Button type="primary" disabled={busy || status !== 'ready'} onClick={onApply}>Apply</Button>
        <Button type="text" disabled={busy || status !== 'ready'} onClick={onDismiss}>Dismiss</Button>
      </div>
    </div>
  );
};

export default StructuredProposalCard;
